import type { ForwardedRef, HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { InjectComponent } from '$util/InjectComponent';
import { Preloader } from '$components/basic/Preloader';
import { Image } from '$components/basic/Image';
import { useAppContext } from '$util/AppContext';
import { renderRichTextByKey } from '$cms/getDataByKey';
import type { CmsContentProps } from '$cms/index';
import { Styled } from './styled';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SuggestionsContainerRef = any;

export enum SuggestionsContainerInjectName {
  LOADING = 'loading',
  NO_RESULTS = 'no-results',
}

export interface SuggestionsContainerProps extends HTMLAttributes<HTMLDivElement> {
  readonly value: string;
  readonly isLoading?: boolean;
  readonly isEmpty: boolean;
}

export const SuggestionsContainer = forwardRef<SuggestionsContainerRef, SuggestionsContainerProps>(
  ({ value, isLoading = false, isEmpty, children, className, ...props }, ref) => {
    const {
      cmsContent: { dictionary },
    } = useAppContext<CmsContentProps>();

    const getSearchboxRichText = renderRichTextByKey.bind(dictionary);

    /**
     * Needed by Autosuggest to use with forwardRef.
     */
    const callRef: ForwardedRef<SuggestionsContainerRef> = (isolatedScroll) => {
      if (isolatedScroll !== null && typeof ref === 'function') {
        ref(isolatedScroll.component);
      }
    };

    let render = children;
    if (isLoading) {
      render = (
        <InjectComponent name={SuggestionsContainerInjectName.LOADING} namespace="SuggestionsContainer">
          <Preloader show />
        </InjectComponent>
      );
    } else if (isEmpty)
      render = (
        <InjectComponent name={SuggestionsContainerInjectName.NO_RESULTS} namespace="SuggestionsContainer">
          <div className="no-results">
            <Image
              provider="cloudinary"
              src="/icons/no-search-results_qiieok.svg"
              alt="no results"
              layout="fixed"
              width="63"
              height="60"
            />
            {getSearchboxRichText(
              'searchBox.noResultMessage.extended',
              <>
                <h3>Sorry, no results found</h3>
                <p>
                  We couldn&apos;t find a match for <i>{value}</i>
                  <br />
                  Please try again
                </p>
              </>,
              { value: `"${value}"` }
            )}
          </div>
        </InjectComponent>
      );
    return (
      <Styled
        ref={callRef}
        className={classNames(
          'SuggestionsContainer',
          isLoading && '-loading',
          isEmpty && '-empty',
          className
        )}
        {...props}
      >
        {render}
      </Styled>
    );
  }
);
SuggestionsContainer.displayName = 'SuggestionsContainer';
